import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../contexts/NotificationsContext";
import { htmlToPlainText } from "../utils/sanitize";
import "./Notifications.css";

const BellIcon = ({ filled }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
    <path d="M13.73 21a2 2 0 01-3.46 0"></path>
  </svg>
);

const TypeIcon = ({ type }) => {
  if (type === 'comment_added') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
      </svg>
    );
  }
  if (type === 'question_added') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="12" r="10"></circle>
        <path d="M9.09 9a3 3 0 1 1 5.82 1c0 2-3 2-3 4"></path>
        <line x1="12" y1="17" x2="12" y2="17"></line>
      </svg>
    );
  }
  return <BellIcon />;
};

function useOutsideClick(ref, handler) {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) return;
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

export default function NotificationsBell() {
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  useOutsideClick(containerRef, () => setOpen(false));

  // Close panel when route changes by listening to popstate/hashchange
  useEffect(() => {
    const close = () => setOpen(false);
    window.addEventListener("popstate", close);
    window.addEventListener("hashchange", close);
    return () => {
      window.removeEventListener("popstate", close);
      window.removeEventListener("hashchange", close);
    };
  }, []);

  const items = notifications || [];

  const onView = useCallback(async (n) => {
    const data = n.data || {};
    const projectId = data.project_id;
    setOpen(false);
    if (projectId) {
      const state = { scrollToQuestionId: data.question_id || null, scrollToCommentId: data.comment_id || null };
      const search = window.location.search || "";
      navigate(`/projects/${projectId}${search}`, { state });
    }
    try { await markAsRead(n.id); } catch {}
  }, [navigate, markAsRead]);

  const handleItemClick = onView;

  const NotificationItem = memo(function NotificationItem({ n, onClick }) {
    return (
      <button className="notif-item" onClick={onClick}>
        <div className="notif-item-left">
          <div className={`notif-item-icon ${n.type}`}>
            <TypeIcon type={n.type} />
          </div>
        </div>
        <div className="notif-item-content">
          {n.data?.content && (
            <div className="notif-item-snippet">{htmlToPlainText(n.data.content)}</div>
          )}
          <div className="notif-item-bottom">
            {(n.data?.author_name || n.data?.creator_name) && (
              <div className="notif-item-author">{n.data.author_name || n.data.creator_name}</div>
            )}
            {n._displayTime && <div className="notif-item-time">{n._displayTime}</div>}
          </div>
        </div>
        <div className="notif-item-cta">View</div>
      </button>
    );
  });

  return (
    <div className="notif-container" ref={containerRef}>
      <button className="notif-bell-btn" aria-label="Notifications" onClick={() => setOpen((v) => !v)}>
        <BellIcon />
        {unreadCount > 0 && <span className="notif-badge" aria-label={`${unreadCount} unread`}>{unreadCount > 9 ? '9+' : unreadCount}</span>}
      </button>

      {open && (
        <div className="notif-panel" role="dialog" aria-label="Notifications">
          <div className="notif-header">
            <span className="notif-title">Notifications</span>
            <div className="notif-actions">
              <button className="btn-link" disabled={loading || unreadCount === 0} onClick={markAllAsRead}>Mark all as read</button>
            </div>
          </div>

          <div className="notif-list">
            {loading && notifications.length === 0 && (
              <div className="notif-empty">Loading...</div>
            )}
            {!loading && items.length === 0 && (
              <div className="notif-empty">You're all caught up</div>
            )}

            {items.map((n) => (
              <NotificationItem key={n.id} n={n} onClick={() => handleItemClick(n)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
